import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Mail, User as UserIcon, Users, Building2 } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Logo } from "../components/Navbar";
import GoogleIcon from "../components/GoogleIcon";
import { googleAuthUrl, facebookAuthUrl, roleDest } from "../lib/social";
import { usePageTitle } from "../lib/usePageTitle";
import { siteLang } from "../lib/siteLang";

const input = "w-full h-12 px-4 rounded-lg bg-[#0D0D0D] border border-[#2A2A2A] text-white text-[16px] placeholder-neutral-600 focus:outline-none focus:border-[#FF5C00] transition-colors";

const ROLES = [
  { id: "client", title: "I'm booking talent", desc: "Post events & book talent", icon: UserIcon },
  { id: "talent", title: "I'm a talent", desc: "Get booking requests", icon: Users },
  { id: "agency", title: "I'm an agency", desc: "Manage a roster of talents", icon: Building2 },
];

// Turn any FastAPI error payload into a clean, user-facing string.
// FastAPI 422 validation returns an array of {msg} objects; rendering that raw would crash React.
export const formatApiError = (err, fallback = "Something went wrong. Please try again.") => {
  const detail = err?.response?.data?.detail;
  if (detail == null) return err?.message && !err.response ? "Network error. Please check your connection." : fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : "")).filter(Boolean).join(" ") || fallback;
  if (typeof detail?.msg === "string") return detail.msg;
  return fallback;
};

const SignIn = () => {
  const [step, setStep] = useState("entry"); // entry | login_password | set_password | role | forgot_sent
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const { user, loginWithToken, setUser } = useAuth();
  const navigate = useNavigate();
  const refCode = new URLSearchParams(window.location.search).get("ref") || "";
  const _qp0 = new URLSearchParams(window.location.search);
  const presetRole = (_qp0.get("role") || "").toLowerCase();
  const rosterId = _qp0.get("roster") || "";
  const hasPresetRole = ["client", "talent", "agency"].includes(presetRole);

  useEffect(() => {
    if (user && user.role) navigate(roleDest(user.role), { replace: true });
  }, [user, navigate]);

  // Prefill email captured on the /join campaign page. Never auto-jump steps (that used to strand users).
  useEffect(() => {
    const qp = new URLSearchParams(window.location.search);
    const qpEmail = qp.get("email");
    const prefill = qpEmail || localStorage.getItem("ts_prefill_email");
    if (prefill && !user) { setEmail(prefill); localStorage.removeItem("ts_prefill_email"); }
    localStorage.removeItem("ts_pending_email"); // clear any stale legacy OTP state
  }, []); // eslint-disable-line

  const handleGoogle = () => { window.location.href = googleAuthUrl(); };
  const handleFacebook = () => { window.location.href = facebookAuthUrl(); };

  const doIdentify = async (e) => {
    e?.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      const { data } = await api.post("/auth/identify", { email });
      if (data.exists) { setPassword(""); setStep("login_password"); }
      else { setPassword(""); setConfirm(""); setStep("set_password"); }
    } catch (err) { toast.error(formatApiError(err, "We could not check that email. Please try again.")); }
    setBusy(false);
  };

  // New user creates a password -> account is created + signed in -> pick a role.
  const savePassword = async (e) => {
    e.preventDefault();
    if (password.length < 8) { toast.error("Password must be at least 8 characters."); return; }
    if (password !== confirm) { toast.error("The two passwords do not match."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/signup", { email, password, ref: refCode || undefined, language: siteLang() });
      loginWithToken(data.token, data.user);
      if (hasPresetRole) { pickRole(presetRole); }
      else { setStep("role"); }
    } catch (err) {
      if (err.response?.status === 409) {
        toast.info("You already have an account. Please sign in.");
        setPassword(""); setStep("login_password");
      } else {
        toast.error(formatApiError(err, "We could not create your account. Please try again."));
      }
    }
    setBusy(false);
  };

  const pickRole = async (role) => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/set-role", { role, roster: rosterId || undefined });
      setUser(data);
      toast.success("Your account is ready.");
      navigate(roleDest(role), { replace: true });
    } catch (err) { toast.error(formatApiError(err, "Could not set your account type.")); setBusy(false); }
  };

  const doLogin = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      loginWithToken(data.token, data.user);
      toast.success("Welcome back.");
      navigate(data.user.role ? roleDest(data.user.role) : "/signin", { replace: true });
    } catch (err) {
      // Social-only account trying a password: route them to the social buttons.
      if (err.response?.headers?.["x-error-code"] === "social_account") toast.info(formatApiError(err));
      else toast.error(formatApiError(err, "Incorrect email or password."));
    }
    setBusy(false);
  };

  const sendForgot = async () => {
    setBusy(true);
    try { await api.post("/auth/forgot-password", { email }); setStep("forgot_sent"); }
    catch (err) { toast.error(formatApiError(err, "Could not send the reset email. Please try again.")); }
    setBusy(false);
  };

  const Header = ({ title, sub }) => (
    <div className="text-center">
      <h1 className="text-[24px] font-bold text-white">{title}</h1>
      {sub && <p className="text-[#9CA3AF] mt-2 text-[14px]">{sub}</p>}
    </div>
  );

  usePageTitle("Sign in");
  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-6 py-16" style={{ background: 'radial-gradient(ellipse at center, rgba(255,92,0,0.08) 0%, #0D0D0D 65%)', minHeight: '100vh' }}>
      <div className="mb-8"><Logo /></div>
      <div className="flex items-center justify-center w-full">
        <div className="w-full max-w-[420px] rounded-2xl border border-[#2A2A2A] bg-[#1A1A1A] p-10" data-testid="signin-card">

          {step === "entry" && (
            <>
              <Header title="Welcome to Talentsio" sub="Sign in or create your account." />
              <div className="mt-7">
                <button onClick={handleGoogle} data-testid="google-btn" style={{ backgroundColor: "#1A1A1A", border: "1px solid #2A2A2A", color: "#FFFFFF" }} className="w-full flex items-center justify-center gap-2 py-3 rounded-lg font-medium hover:border-gray-500 transition-colors">
                  <img src="https://www.google.com/favicon.ico" alt="" className="w-4 h-4" onError={(e) => (e.target.style.display = "none")} />
                  Google
                </button>
                {/* Facebook login temporarily disabled - re-enable when app is published
                <button onClick={handleFacebook} data-testid="facebook-btn" style={{ backgroundColor: "#1A1A1A", border: "1px solid #2A2A2A", color: "#FFFFFF" }} className="flex items-center justify-center gap-2 py-3 rounded-lg font-medium hover:border-gray-500 transition-colors">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="#1877F2"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                  Facebook
                </button>
                */}
              </div>
              <div className="flex items-center gap-4 my-5">
                <div className="flex-1 h-px bg-[#2A2A2A]" /><span className="text-[13px] text-neutral-600">or</span><div className="flex-1 h-px bg-[#2A2A2A]" />
              </div>
              <form onSubmit={doIdentify} className="space-y-3">
                <input className={input} type="email" placeholder="you@email.com" value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus data-testid="email-input" />
                <button disabled={busy} data-testid="continue-email-btn" style={{ backgroundColor: "#FF5C00" }} className="w-full h-12 rounded-lg text-white font-semibold flex items-center justify-center gap-2 transition-colors disabled:opacity-60">
                  {busy ? <Loader2 size={16} className="animate-spin" /> : <Mail size={16} />} Continue with email
                </button>
              </form>
              <p className="text-center text-[12px] text-neutral-600 mt-4">By continuing, you agree to our <Link to="/terms" target="_blank" className="text-[#FF7A2F] underline">Terms of Service</Link> and <Link to="/privacy" target="_blank" className="text-[#FF7A2F] underline">Privacy Policy</Link>.</p>
            </>
          )}

          {step === "login_password" && (
            <>
              <button onClick={() => { setStep("entry"); setPassword(""); }} className="flex items-center gap-1.5 text-[13px] text-neutral-500 hover:text-white mb-4" data-testid="back-to-entry"><ArrowLeft size={15} /> Back</button>
              <Header title="Welcome back" sub={email} />
              <form onSubmit={doLogin} className="space-y-3 mt-6">
                <input className={input} type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} autoFocus required data-testid="login-password-input" />
                <button disabled={busy} data-testid="login-submit-btn" style={{ backgroundColor: "#FF5C00" }} className="w-full h-12 rounded-lg text-white font-semibold flex items-center justify-center gap-2 transition-colors disabled:opacity-60">
                  {busy && <Loader2 size={16} className="animate-spin" />} Sign in
                </button>
              </form>
              <button onClick={sendForgot} disabled={busy} data-testid="forgot-password-link" className="mt-3 text-[13px] text-[#FF7A2F] hover:underline">Forgot password?</button>
              <div className="flex items-center gap-4 my-5">
                <div className="flex-1 h-px bg-[#2A2A2A]" /><span className="text-[12px] text-neutral-600">or continue with</span><div className="flex-1 h-px bg-[#2A2A2A]" />
              </div>
              <div className="flex gap-3">
                <button onClick={handleGoogle} data-testid="login-google-btn" className="flex-1 h-11 flex items-center justify-center gap-2 rounded-lg bg-[#181818] border border-[#2A2A2A] text-neutral-300 text-[14px] font-medium hover:border-[#3A3A3A] hover:text-white transition-colors">
                  <GoogleIcon /> Google
                </button>
                {/* Facebook login temporarily disabled - re-enable when app is published
                <button onClick={handleFacebook} data-testid="login-facebook-btn" className="flex-1 h-11 flex items-center justify-center gap-2 rounded-lg bg-[#181818] border border-[#2A2A2A] text-neutral-300 text-[14px] font-medium hover:border-[#1877F2] hover:text-white transition-colors">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="#1877F2"><path d="M24 12.07C24 5.4 18.63 0 12 0S0 5.4 0 12.07C0 18.1 4.39 23.1 10.13 24v-8.44H7.08v-3.49h3.05V9.41c0-3.02 1.79-4.69 4.53-4.69 1.31 0 2.68.24 2.68.24v2.97h-1.51c-1.49 0-1.96.93-1.96 1.89v2.25h3.33l-.53 3.49h-2.8V24C19.61 23.1 24 18.1 24 12.07z"/></svg>
                  Facebook
                </button>
                */}
              </div>
            </>
          )}

          {step === "set_password" && (
            <>
              <button onClick={() => { setStep("entry"); setPassword(""); setConfirm(""); }} className="flex items-center gap-1.5 text-[13px] text-neutral-500 hover:text-white mb-4" data-testid="back-from-setpw"><ArrowLeft size={15} /> Back</button>
              <Header title="Create your password" sub={`New account for ${email}`} />
              <form onSubmit={savePassword} className="space-y-3 mt-6">
                <input className={input} type="password" placeholder="New password (min 8 characters)" value={password} onChange={(e) => setPassword(e.target.value)} autoFocus required data-testid="set-password-input" />
                <input className={input} type="password" placeholder="Confirm password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required data-testid="set-password-confirm" />
                {confirm && password !== confirm && <p className="text-[13px] text-[#ff6a4d]" data-testid="password-mismatch">The two passwords do not match</p>}
                <button disabled={busy} data-testid="set-password-btn" style={{ backgroundColor: "#FF5C00" }} className="w-full h-12 rounded-lg text-white font-semibold flex items-center justify-center gap-2 transition-colors disabled:opacity-60">
                  {busy && <Loader2 size={16} className="animate-spin" />} Continue
                </button>
              </form>
            </>
          )}

          {step === "role" && (
            <>
              <Header title="What best describes you?" sub="Pick how you'll use Talentsio" />
              <div className="space-y-3 mt-6" data-testid="role-cards">
                {ROLES.map((r) => (
                  <button key={r.id} onClick={() => pickRole(r.id)} disabled={busy} data-testid={`role-${r.id}`}
                    className="w-full flex items-center gap-3 p-4 rounded-xl border border-[#2A2A2A] bg-[#0D0D0D] hover:border-[#FF5C00] text-left transition-colors disabled:opacity-60">
                    <div className="w-10 h-10 rounded-lg bg-[#FF5C00]/12 flex items-center justify-center text-[#FF7A2F] shrink-0"><r.icon size={20} /></div>
                    <div><div className="text-[15px] font-semibold text-white">{r.title}</div><div className="text-[13px] text-neutral-500">{r.desc}</div></div>
                  </button>
                ))}
              </div>
            </>
          )}

          {step === "forgot_sent" && (
            <>
              <div className="w-12 h-12 rounded-full bg-[#1A8A4A]/15 flex items-center justify-center mx-auto mb-4"><Mail size={22} className="text-[#2fbe6e]" /></div>
              <Header title="Check your email" sub={`If an account exists for ${email}, we've sent a password reset link. It expires in 1 hour.`} />
              <button onClick={() => { setStep("entry"); setPassword(""); }} data-testid="forgot-back-btn" className="mt-6 w-full h-12 rounded-lg bg-[#181818] border border-[#2A2A2A] text-neutral-300 font-medium hover:text-white hover:border-[#3A3A3A] transition-colors">Back to sign in</button>
            </>
          )}

          <p className="text-center text-[13px] text-neutral-600 mt-6"><Link to="/" className="hover:text-neutral-400">Back to home</Link></p>
        </div>
      </div>
    </div>
  );
};

export default SignIn;
